import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, getUserModules, hasModuleAccess, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";
import ProtectedRoute from "./components/ProtectedRoute";
import StaffLayout from "./components/StaffLayout";
import PortalLayout from "./components/PortalLayout";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";

import Dashboard from "./pages/staff/Dashboard";
import DoctorDashboard from "./pages/staff/DoctorDashboard";
import Enquiries from "./pages/staff/Enquiries";
import Patients from "./pages/staff/Patients";
import PatientDetail from "./pages/staff/PatientDetail";
import Appointments from "./pages/staff/Appointments";
import Treatments from "./pages/staff/Treatments";
import Invoices from "./pages/staff/Invoices";
import Reminders from "./pages/staff/Reminders";
import Doctors from "./pages/staff/Doctors";
import Settings from "./pages/staff/Settings";
import CalendarView from "./pages/staff/CalendarView";
import TreatmentTemplates from "./pages/staff/TreatmentTemplates";
import Reports from "./pages/staff/Reports";
import StaffRoles from "./pages/staff/StaffRoles";

import PortalDashboard from "./pages/portal/PortalDashboard";
import MyAppointments from "./pages/portal/MyAppointments";
import MyHistory from "./pages/portal/MyHistory";
import MyInvoices from "./pages/portal/MyInvoices";
import NewEnquiry from "./pages/portal/NewEnquiry";
import BookAppointment from "./pages/portal/BookAppointment";

import "./App.css";

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "patient" ? "/portal" : "/staff"} replace />;
}

function StaffHome() {
  const { user } = useAuth();
  if (!user) return null;

  if (!hasModuleAccess(user, "dashboard")) {
    const modules = getUserModules(user);
    const fallbackMap = {
      calendar: "/staff/calendar",
      enquiries: "/staff/enquiries",
      patients: "/staff/patients",
      appointments: "/staff/appointments",
      reports: "/staff/reports",
      treatments: "/staff/treatments",
      invoices: "/staff/invoices",
      reminders: "/staff/reminders",
      templates: "/staff/templates",
      doctors: "/staff/doctors",
      settings: "/staff/settings",
      staff_roles: "/staff/staff-roles",
    };
    const firstAllowed = modules.map((module) => fallbackMap[module]).find(Boolean);
    return <Navigate to={firstAllowed || "/login"} replace />;
  }

  if (user?.role === "doctor") return <DoctorDashboard />;
  return <Dashboard />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/app" element={<RoleRedirect />} />

            {/* Staff/Admin */}
            <Route
              path="/staff"
              element={
                <ProtectedRoute roles={["admin", "staff", "doctor"]}>
                  <StaffLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<StaffHome />} />
              <Route path="calendar" element={<ProtectedRoute module="calendar"><CalendarView /></ProtectedRoute>} />
              <Route path="enquiries" element={<ProtectedRoute module="enquiries"><Enquiries /></ProtectedRoute>} />
              <Route path="patients" element={<ProtectedRoute module="patients"><Patients /></ProtectedRoute>} />
              <Route path="patients/:id" element={<ProtectedRoute module="patients"><PatientDetail /></ProtectedRoute>} />
              <Route path="appointments" element={<ProtectedRoute module="appointments"><Appointments /></ProtectedRoute>} />
              <Route path="reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
              <Route path="treatments" element={<ProtectedRoute module="treatments"><Treatments /></ProtectedRoute>} />
              <Route path="invoices" element={<ProtectedRoute module="invoices"><Invoices /></ProtectedRoute>} />
              <Route path="reminders" element={<ProtectedRoute module="reminders"><Reminders /></ProtectedRoute>} />
              <Route path="staff-roles" element={<ProtectedRoute module="staff_roles"><StaffRoles /></ProtectedRoute>} />
              <Route path="doctors" element={<ProtectedRoute module="doctors"><Doctors /></ProtectedRoute>} />
              <Route path="templates" element={<ProtectedRoute module="templates"><TreatmentTemplates /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
            </Route>

            {/* Patient portal */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute roles={["patient"]}>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<PortalDashboard />} />
              <Route path="book" element={<BookAppointment />} />
              <Route path="appointments" element={<MyAppointments />} />
              <Route path="history" element={<MyHistory />} />
              <Route path="invoices" element={<MyInvoices />} />
              <Route path="enquiry" element={<NewEnquiry />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
